import React, { useCallback } from "react";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { Usb, CheckCircle2 } from "lucide-react";
import JSZip from "jszip";
import LFS from "@isomorphic-git/lightning-fs";
import dayjs from "dayjs";
import { notifyStudent, publishBranch, publishBranchWithRequest } from "@/api";


import {
  useEditorState,
} from "@/store";
import {
  CodeFolder,
  DownloadIcon,
  PublishIcon,
  SendIcon,
  UploadIcon,
} from "./shared/icons";
import { type IParentProps } from "../types";
import {
  Dropdown,
  FloatButton,
  message,
  Input,
  Checkbox,
  Button
} from "antd";
import { BlocklyInvalidXml, BlocklyWorkspaceXmlMismatch, Logger, UploadError } from "../utils";
import { FS } from "@/singletons/FileSystem";
import { Toolkit } from "@/components";
import * as Sentry from "@sentry/react";

const TEXT_EXT = new Set([
  "txt",
  "md",
  "json",
  "js",
  "ts",
  "tsx",
  "jsx",
  "css",
  "scss",
  "sass",
  "less",
  "html",
  "xml",
  "yml",
  "yaml",
  "csv",
  "svg",
  "env",
  "ini",
  "conf",
  "py",
  "rb",
  "php",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "cs",
  "sh",
  "ino",
]);

const RIDERS_HEADER_PYTHON = "# RIDERS_FILE_ID: ";
const RIDERS_HEADER_XML = "<!-- RIDERS_FILE_ID: ";
const RIDERS_HEADER_XML_END = " -->";
const RIDERS_HEADER_INO = "// RIDERS_FILE_ID: ";

const addHeader = (name: string, content: string) => {
  const ext = extOf(name);
  const fileName = name.split("/").pop() || name;
  if (ext === "py") {
    return `${RIDERS_HEADER_PYTHON}${fileName}\n${content}`;
  } else if (ext === "xml") {
    return `${RIDERS_HEADER_XML}${fileName}${RIDERS_HEADER_XML_END}\n${content}`;
  } else if (ext === "ino") {
    return `${RIDERS_HEADER_INO}${fileName}\n${content}`;
  }
  return content;
};

const extractHeader = (content: string) => {
  if (content.startsWith(RIDERS_HEADER_PYTHON)) {
    const lines = content.split("\n");
    const header = lines[0];
    const fileName = header.replace(RIDERS_HEADER_PYTHON, "").trim();
    const cleanContent = lines.slice(1).join("\n");
    return { fileName, cleanContent };
  } else if (content.startsWith(RIDERS_HEADER_XML)) {
    const lines = content.split("\n");
    const header = lines[0];
    const fileName = header
      .replace(RIDERS_HEADER_XML, "")
      .replace(RIDERS_HEADER_XML_END, "")
      .trim();
    const cleanContent = lines.slice(1).join("\n");
    return { fileName, cleanContent };
  } else if (content.startsWith(RIDERS_HEADER_INO)) {
    const lines = content.split("\n");
    const header = lines[0];
    const fileName = header.replace(RIDERS_HEADER_INO, "").trim();
    const cleanContent = lines.slice(1).join("\n");
    return { fileName, cleanContent };
  }
  return null;
};

const isZipFile = (f: File) =>
  f?.type === "application/zip" || f?.name?.toLowerCase()?.endsWith(".zip");

const extOf = (name: string) => {
  const ix = name.lastIndexOf(".");
  return ix === -1 ? "" : name.slice(ix + 1).toLowerCase();
};

// basit path birleştirme (base: "dir/", rel: "a/b.txt" -> "dir/a/b.txt")
const joinPath = (base: string, rel: string) =>
  (base?.endsWith("/") ? base.slice(0, -1) : base) +
  "/" +
  rel.replace(/^\/+/, "");

export interface MenuComponentProps extends IParentProps {
  clickAndCloseHandler?: (func: () => void) => any;
  handleLanguageChange?: (lang: string) => void;
  showModal: () => void;
}

export const FloatMenu: React.FC<MenuComponentProps> = () => {
  const [compiling, setCompiling] = React.useState(false);
  const { showModal }: any = React.useContext(Toolkit.context);
  const {
    metadata,
    platform,
    project: { project_type: projectType } = {},
    tokens: { express_api_token: accessToken } = {},
    blocklyCodePath,
    pythonCodePath,
    setBlocklyRefreshKey
  } = useEditorState();
  const { t } = useTranslation();

  const userCodePaths: string[] = [];
  if (blocklyCodePath) {
    userCodePaths.push(blocklyCodePath);
  }
  if (pythonCodePath) {
    userCodePaths.push(pythonCodePath);
  }

  const [messageApi, contextHolder] = message.useMessage();

  const publish_branch = useCallback(async (message: string) => {
    messageApi.open({
      key: "publish",
      type: "loading",
      content: t("branchPublishing"),
      duration: 60,
      className: "messagebox",
    });

    try {
      await publishBranch(accessToken || "", message);
      messageApi.open({
        key: "publish",
        type: "success",
        content: t("branchPublished"),
        duration: 2,
        className: "messagebox",
      });
    } catch (error) {
      messageApi.open({
        key: "publish",
        type: "error",
        content: t("branchNotPublished"),
        duration: 2,
        className: "messagebox",
      });
      Sentry.captureException(new UploadError("Publish branch error", error as Error));
    }
  }, [accessToken]);

  const publish_branch_with_request = useCallback(async (message: string, hasRequest: boolean) => {
    messageApi.open({
      key: "publish",
      type: "loading",
      content: t("branchPublishing"),
      duration: 60,
      className: "messagebox",
    });

    try {
      await publishBranchWithRequest(accessToken || "", message, hasRequest);
      messageApi.open({
        key: "publish",
        type: "success",
        content: t("branchPublished"),
        duration: 2,
        className: "messagebox",
      });
    } catch (error) {
      messageApi.open({
        key: "publish",
        type: "error",
        content: t("branchNotPublished"),
        duration: 2,
        className: "messagebox",
      });
      Sentry.captureException(new UploadError("Publish branch error", error as Error));
    }
  }, [accessToken]);


  let fileSelector: HTMLInputElement | undefined = undefined;

  const handleUpload = () => {
    if (typeof fileSelector === "undefined") {
      fileSelector = document.createElement("input");
      fileSelector.type = "file";
      fileSelector.style.display = "none";
      document.body.appendChild(fileSelector);
    }

    fileSelector.onchange = async () => {
      try {
        const file = fileSelector?.files?.[0];
        if (!file) return;

        if (isZipFile(file)) {
          const buffer = await file.arrayBuffer();
          const zip = await JSZip.loadAsync(buffer);

          const writes: Promise<any>[] = [];

          let isTargetFound = false
          zip.forEach((relativePath, entry) => {
            if (entry.dir) return;
            const isText = TEXT_EXT.has(extOf(entry.name));

            const p = entry
              .async(isText ? "string" : "uint8array")
              .then((content: string | Uint8Array) => {
                let targetFile = userCodePaths.find((f) => f.includes(relativePath));
                let finalContent = content;

                if (isText && typeof content === "string") {
                  const extracted = extractHeader(content);
                  if (extracted) {
                    const { fileName, cleanContent } = extracted;
                    // Always use clean content if a header was found
                    finalContent = cleanContent;

                    const matchByHeader = userCodePaths.find((f) =>
                      f.endsWith("/" + fileName) || f === fileName
                    );
                    if (matchByHeader) {
                      targetFile = matchByHeader;
                    }
                  }
                }

                if (targetFile) {
                  if (targetFile === blocklyCodePath) {
                    try {
                      isTargetFound = true;
                      window.Blockly.utils.xml.textToDom(finalContent)
                      return FS.write(targetFile, String(finalContent));

                    } catch (e) {
                      messageApi.error({ content: t("blockly.invalidXml"), duration: 5, key: "blockly-error" });
                      Sentry.captureException(new BlocklyInvalidXml("Blockly invalid XML", e as Error));
                      throw e
                    }
                  } else if (targetFile === pythonCodePath) {
                    isTargetFound = true;
                    return FS.write(targetFile, String(finalContent));
                  }
                }
              });

            writes.push(p);
          });

          await Promise.all(writes);

          useEditorState.setState({ isBlocklyRefreshValid: true });
          setBlocklyRefreshKey();

          if (!isTargetFound) {
            messageApi.error({ content: t("blockly.MatchingFileNotFound"), duration: 5, key: "blockly-error" });
            Sentry.captureException(new BlocklyWorkspaceXmlMismatch("Blockly matching file not found"));
          }
        } else {
          let text = await file.text();
          const extracted = extractHeader(text);
          if (extracted) {
            text = extracted.cleanContent;
          }


          const fileExtention = file.name.split(".").pop()
          let path = null;
          if (fileExtention === "xml") {
            path = blocklyCodePath;
            try {
              window.Blockly.utils.xml.textToDom(text)
              await FS.write(path, text);
              useEditorState.setState({ isBlocklyRefreshValid: true });
              setBlocklyRefreshKey();
            } catch (e) {
              messageApi.error({ content: t("blockly.invalidXml"), duration: 5, key: "blockly-error" });
              Sentry.captureException(new BlocklyInvalidXml("Blockly invalid XML", e as Error));
              throw e
            }

          } else if (fileExtention && TEXT_EXT.has(fileExtention)) {
            path = pythonCodePath;
            await FS.write(path, text);
            useEditorState.setState({ isBlocklyRefreshValid: true });
            setBlocklyRefreshKey();
          } else {
            messageApi.error({ content: t("blockly.MatchingFileNotFound"), duration: 5, key: "blockly-error" });
            Sentry.captureException(new BlocklyWorkspaceXmlMismatch("Blockly matching file not found"));

          }
        }
      } catch (err) {
        Sentry.captureException(new UploadError("Upload error", err as Error));
      } finally {
        if (fileSelector && document.body.contains(fileSelector)) {
          document.body.removeChild(fileSelector);
        }
        fileSelector = undefined; // bir sonraki sefer sıfırdan oluşturulsun
      }
    };

    fileSelector.click();
  };

  const handleDownload = async () => {
    const zip = new JSZip();

    const url = new URL(window.location.href)

    for (const userCodePath of userCodePaths) {

      const raw = await FS.read(userCodePath);
      if (raw === null) continue;
      const content = addHeader(userCodePath, raw);
      zip.file(userCodePath.split("/").pop()!, content, {
        comment: userCodePath,
      });
    }

    try {
      const downloadIncludeFilesNames = await FS.list("/download_include") || [];
      for (const fileName of downloadIncludeFilesNames) {
        const file = await FS.read("/download_include/" + fileName) || "";
        zip.file(fileName, file);
      }
    } catch (error) {
      Sentry.captureException(new UploadError("Failed to read download include files", error as Error));
    }

    zip.generateAsync({ type: "blob" }).then(function (content) {
      // see FileSaver.js
      saveAs(content, (url.origin.includes("mebkit") ? "mebkit_" : "riders_") + dayjs().format("YYYYMMDD_HHmmss") + ".zip");
    });

  };

  const hasIno = userCodePaths.find((file) => {
    if (!file) {
      return false;
    }

    if (file.split(".").pop() === "ino") {
      return file;
    } else {
      return false;
    }
  });

  const handleUploadArduino = async (path: string) => {
    const isChrome = typeof (window as any).chrome !== "undefined";

    if (!isChrome) {
      message.info({
        content: "Please use Chrome for compiling",
        key: "hex",
        duration: 2,
      });
    }

    if (compiling || !isChrome) {
      return;
    }

    setCompiling(true);
    try {

      message.loading({
        content: t("uploadToArduinoCompilingAction"),
        key: "hex",
        duration: 0,
      });

      const code = await FS.read(path);
      const uri =
        "https://141euep68f.execute-api.eu-west-1.amazonaws.com/default/ino2HexConverter";
      const ino_content = JSON.stringify({ ino_content: code });
      const response = await fetch(uri, {
        method: "POST",
        body: JSON.stringify({ body: ino_content }),
        headers: {
          "Content-Type": "application/json",
        },
      }).then((resp) => resp.json());

      const body = JSON.parse(response.body);

      if (body.hex) {
        const uploaderFS = new LFS("riders_fs").promises;
        await uploaderFS.mkdir("/.build").catch(() => { });
        await uploaderFS.writeFile("/.build/ino.hex", String(body.hex));

        message.open({
          type: "success",
          icon: <></>,
          key: "hex",
          duration: 0,
          content: (
            <div className="text-left w-[300px] py-1">
              <div className="flex items-center gap-2">
                <CheckCircle2
                  size={16}
                  className="shrink-0 text-emerald-500"
                />
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {t("uploadToArduinoCompiledTitle")}
                </span>
              </div>
              <p className="mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400">
                {t("uploadToArduinoCompiledDescription")}
              </p>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="small" onClick={() => message.destroy("hex")}>
                  {t("cancel")}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<Usb size={14} />}
                  onClick={() => {
                    window.document.getElementById("uploadhex")?.click();
                    message.destroy("hex");
                  }}
                >
                  {t("userCodeUploadAction")}
                </Button>
              </div>
            </div>
          ),
        });

      } else {
        message.error({
          content: t("uploadToArduinoCompileFailed"),
          key: "hex",
          duration: 2,
        });
      }
    } catch (error) {
      message.error({
        content: t("uploadToArduinoCompileFailed"),
        key: "hex",
        duration: 2,
      });
    } finally {
      setCompiling(false);
    }
  };

  const handleSendNotification = async () => {
    message.loading({
      content: t("sendingNotification"),
      key: "notification",
      duration: 0,
    });
    await notifyStudent(accessToken || "", {
      classroom: metadata?.cms?.cms_info.classroom_slug || "",
      courseSlug: metadata?.cms?.course_slug || "",
    });
    message.success({
      content: t("notificationSent"),
      key: "notification",
      duration: 2,
    });
  };

  if (!blocklyCodePath && !pythonCodePath) {
    return null;
  }

  const getDropdownItems = () => {
    const items = [
      {
        key: "download",
        label: (
          <Item>
            <DownloadIcon /> {t("userCodeDownloadAction")}
          </Item>
        ),
        onClick: handleDownload,
      },
      {
        key: "upload",
        label: (
          <Item>
            <UploadIcon /> {t("userCodeUploadAction")}
          </Item>
        ),
        onClick: handleUpload,
      },
    ];

    // Dont show publish or submit button for preview and lti projects
    if (platform !== "lti" && !metadata?.is_preview) {
      if (metadata?.isProjectOwner) {
        items.push({
          key: "publish",
          label: (
            <Item>
              <PublishIcon /> {t("publish")}
            </Item>
          ),
          onClick: () => {
            if (projectType && ["playable", "design", "review"].includes(projectType)) {
              showModal(
                t("publish"),
                <div className="flex flex-col space-y-2">
                  <PublishTextArea key={Math.random()} />
                  <CreateRequest key={Math.random()} />
                </div>,
                {
                  showFooter: true,
                  cancelText: t("cancel"),
                  okText: t("submit"),
                  onOk: async () => {
                    const input = document.getElementById(
                      "publishTextArea"
                    ) as HTMLTextAreaElement;
                    const check = document.getElementById(
                      "createRequestCheckbox"
                    ) as any;

                    const value = input?.value;
                    console.log(check.checked);
                    await publish_branch_with_request(
                      value || "No message",
                      check.checked || false
                    );
                  },
                }
              );
            } else {
              showModal(
                t("publish"),
                <PublishTextArea key={Math.random()} />,
                {
                  showFooter: true,
                  cancelText: t("cancel"),
                  okText: t("submit"),
                  onOk: async () => {
                    const input = document.getElementById(
                      "publishTextArea"
                    ) as HTMLTextAreaElement;

                    const value = input?.value;

                    await publish_branch(value || "No message");
                  },
                }
              );
            }
          },
        });
      } else {
        items.push({
          key: "send",
          label: (
            <Item>
              <SendIcon /> {t("submit")}
            </Item>
          ),
          onClick: () => handleSendNotification(),
        })
      }
    }

    if (hasIno) {
      items.push({
        key: "arduino",
        label: (
          <Item>
            <Usb size={14} /> {t("uploadToArduinoAction")}
          </Item>
        ),
        onClick: () => {
          handleUploadArduino(hasIno);
        },
      });
    }

    return items;
  }

  return (
    <>
      <Dropdown
        trigger={["click"]}
        menu={{
          items: getDropdownItems()
        }}
      >
        <FloatButton
          icon={<CodeFolder size={20} />}
          type="primary"
          style={{ bottom: 20, right: 10 }}
        ></FloatButton>
      </Dropdown>
      {contextHolder}
    </>
  );
};

const Item = ({ children }: any) => (
  <span className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
    {children}
  </span>
);

const PublishTextArea = () => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState("");

  return (
    <div>
      <Input.TextArea
        id="publishTextArea"
        placeholder={t("publishTextAreaMessage")}
        rows={3}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
};

const CreateRequest = () => {
  const { t } = useTranslation();
  const [value, setValue] = React.useState(false);

  return (
    <div>
      <Checkbox
        id="createRequestCheckbox"
        onChange={(e) => setValue(e.target.checked)}
        value={value}
      >
        {t("createRequestCheckboxMessage")}
      </Checkbox>
    </div>
  );
};
